/**
 * ComparisonTable — semantic <table> with a two-column "X vs Y" layout.
 * AI engines disproportionately cite tables when answering "which is better"
 * or "what's the difference between" questions. Use this on:
 *   • SEO vs PPC
 *   • Local vs national agencies
 *   • Web design tier comparisons
 *   • Service offering breakdowns
 *
 * Renders a single <table> with proper <thead>/<tbody>/<th scope> markup so
 * crawlers and screen readers can parse it cleanly.
 */

import { Check, X, Minus } from "lucide-react";

export type ComparisonRow = {
  /** Row label / criterion */
  label: string;
  /** Cell A — string, true (✓), false (✗), or null (–) */
  a: string | boolean | null;
  /** Cell B — string, true (✓), false (✗), or null (–) */
  b: string | boolean | null;
};

export type ComparisonTableProps = {
  /** Optional caption rendered as <caption> + visible heading */
  caption?: string;
  /** Header label for column A (e.g. "Colorado Web Impressions") */
  headerA: string;
  /** Header label for column B (e.g. "National Agency") */
  headerB: string;
  /** Rows — render in order */
  rows: ComparisonRow[];
};

function renderCell(value: string | boolean | null) {
  if (value === true) {
    return (
      <span className="inline-flex items-center gap-1.5 font-semibold text-emerald-600">
        <Check className="h-4 w-4" /> Yes
      </span>
    );
  }
  if (value === false) {
    return (
      <span className="inline-flex items-center gap-1.5 font-semibold text-red-600">
        <X className="h-4 w-4" /> No
      </span>
    );
  }
  if (value === null) {
    return (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <Minus className="h-4 w-4" /> –
      </span>
    );
  }
  return <span className="text-foreground/90">{value}</span>;
}

export function ComparisonTable({
  caption,
  headerA,
  headerB,
  rows,
}: ComparisonTableProps) {
  return (
    <div className="my-8 overflow-x-auto rounded-2xl border border-border bg-card shadow-card">
      <table className="w-full border-collapse text-sm sm:text-base">
        {caption && (
          <caption className="border-b border-border bg-brand-gold/5 px-5 py-4 text-left text-base font-extrabold text-foreground sm:text-lg">
            {caption}
          </caption>
        )}
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th
              scope="col"
              className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground"
            >
              Criteria
            </th>
            <th
              scope="col"
              className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-brand-gold-deep"
            >
              {headerA}
            </th>
            <th
              scope="col"
              className="px-5 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground"
            >
              {headerB}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.label}
              className={i % 2 === 0 ? "bg-background" : "bg-muted/10"}
            >
              <th
                scope="row"
                className="px-5 py-4 text-left font-semibold text-foreground"
              >
                {row.label}
              </th>
              <td className="px-5 py-4">{renderCell(row.a)}</td>
              <td className="px-5 py-4">{renderCell(row.b)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
